import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import Chart from "./Chart";
import KPITile from "./KPITile";
import EmptyState from "./states/EmptyState";
import ErrorState from "./states/ErrorState";
import Skeleton from "./states/Skeleton";
import {
  accountDetail,
  accountTotals,
  accountsFor,
  accountsSummary,
  allData,
  breakdown,
  buildAccountRecords,
  buildLineRecords,
  categoryRecords,
  completeMonthsOnly,
  creditsBy,
  creditsMonthly,
  creditsSummary,
  divisionMonth,
  divisionSeries,
  divisionTiles,
  divisionsOf,
  drill,
  filterDivisions,
  filterServices,
  frontLoadSummary,
  fuelSurchargeByAccount,
  groupCategories,
  lineLabel,
  lineTiles,
  linesOf,
  miscByDivision,
  monthLabel,
  monthTable,
  monthlyTotals,
  monthsOf,
  movers,
  normalizeRevenueConfig,
  overview,
  previousMonth,
  residentialSummary,
  rollOffSummary,
  selectedMonth,
  toCsv,
  topCreditAccounts,
  topDrivers,
  workingDayView,
  type AccountRecord,
  type CategoryGroup,
  type CategoryRecord,
  type FscProposal,
  type InvoiceGroupRow,
  type LineRecord,
  type RevenueConfig,
} from "../lib/revenueTab.mjs";
import { fetchRevenueAccounts, fetchRevenueLines, RevenueReadError } from "../lib/revenueTabData";
import { useGlobalFilters } from "../lib/globalFilters";
import { type ManifestWidget } from "../lib/manifest";
import { GATE_COPY } from "../lib/sanityGate";
import { useTenantId } from "../lib/tenant";
import { widgetDomain } from "../lib/widgetDomain.mjs";

/**
 * Revenue tab (wave 2): the client's revenue dashboard, rebuilt on the synced
 * invoice lines. One component draws every view; `widget.revenue_tab.view`
 * picks which. Every figure comes from revenueTab.mjs (pinned by
 * scripts/revenue-tab.test.mjs); this file is layout, pickers and sorting.
 *
 * Scope: the page's division chip on every view; the page's service chip on
 * the all-line views (the Roll Off, Front Load and Residential views keep
 * their own population); the page's month for single-month views when that
 * month has ended, else the newest ended month. Only ended months are read.
 */

const PALETTE = [
  "#0F6B5C",
  "#1F4E79",
  "#B45309",
  "#6D28D9",
  "#5FB7A5",
  "#7FA7D6",
  "#9A3412",
  "#475569",
];

const ROW_STEP = 100;

// ---------------------------------------------------------------------------
// Formatting and small parts
// ---------------------------------------------------------------------------

function money(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "-";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}
function signedMoney(n: number): string {
  return `${n > 0 ? "+" : ""}${money(n)}`;
}
function pct(fraction: number | null | undefined, digits = 1): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return "n/a";
  return `${(fraction * 100).toFixed(digits)}%`;
}
function int(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "-";
  return n.toLocaleString("en-US");
}

const th: CSSProperties = {
  textAlign: "right",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "var(--text-2)",
  padding: "6px 8px",
  borderBottom: "1px solid var(--border-subtle)",
  whiteSpace: "nowrap",
};
const td: CSSProperties = {
  textAlign: "right",
  fontSize: 13,
  padding: "5px 8px",
  borderBottom: "1px solid var(--border-subtle)",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};
const left: CSSProperties = { textAlign: "left", whiteSpace: "normal" };
const note: CSSProperties = { fontSize: 11, color: "var(--text-muted)", marginBottom: 6 };
const tile: CSSProperties = {
  border: "1px solid var(--border-subtle)",
  borderRadius: 10,
  padding: "10px 12px",
  background: "var(--bg-card)",
};
const control: CSSProperties = {
  fontSize: 12,
  padding: "4px 8px",
  border: "1px solid var(--border-subtle)",
  borderRadius: 6,
  background: "var(--bg-card)",
  color: "var(--text-1)",
};
const grid = (min: number): CSSProperties => ({
  display: "grid",
  gap: 10,
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
});

function Note(props: { children: ReactNode }) {
  return <div style={note}>{props.children}</div>;
}

function Table(props: {
  head: ReactNode[];
  rows: ReactNode[][];
  foot?: ReactNode[];
  onRowClick?: (i: number) => void;
  onHeadClick?: (i: number) => void;
}) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {props.head.map((h, i) => (
              <th
                key={i}
                style={{
                  ...(i === 0 ? { ...th, ...left } : th),
                  cursor: props.onHeadClick ? "pointer" : undefined,
                }}
                onClick={props.onHeadClick ? () => props.onHeadClick?.(i) : undefined}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.rows.map((r, i) => (
            <tr
              key={i}
              onClick={props.onRowClick ? () => props.onRowClick?.(i) : undefined}
              style={{ cursor: props.onRowClick ? "pointer" : undefined }}
            >
              {r.map((c, j) => (
                <td key={j} style={j === 0 ? { ...td, ...left } : td}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {props.foot ? (
          <tfoot>
            <tr>
              {props.foot.map((c, j) => (
                <td key={j} style={{ ...(j === 0 ? { ...td, ...left } : td), fontWeight: 700 }}>
                  {c}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function download(filename: string, body: string) {
  const blob = new Blob([body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function CsvButton(props: {
  filename: string;
  headers: string[];
  rows: (string | number | null)[][];
}) {
  return (
    <button
      type="button"
      style={{ ...control, cursor: "pointer" }}
      onClick={() => download(props.filename, toCsv(props.headers, props.rows))}
    >
      Export CSV
    </button>
  );
}

function Signed(props: { n: number }) {
  const color =
    props.n > 0 ? "var(--flag-green)" : props.n < 0 ? "var(--flag-red)" : "var(--text-1)";
  return <span style={{ color, fontWeight: 600 }}>{signedMoney(props.n)}</span>;
}

/** A capped list with a "show more" control, so a 10,000-row table stays usable. */
function useRowLimit(resetKey: string): [number, () => void] {
  const [limit, setLimit] = useState(ROW_STEP);
  useEffect(() => setLimit(ROW_STEP), [resetKey]);
  return [limit, () => setLimit((l) => l + ROW_STEP * 5)];
}

function MoreRows(props: { shown: number; total: number; onMore: () => void }) {
  if (props.shown >= props.total) return null;
  return (
    <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
      <span style={note}>
        Showing {int(props.shown)} of {int(props.total)}. Export CSV has every row.
      </span>
      <button type="button" style={{ ...control, cursor: "pointer" }} onClick={props.onMore}>
        Show more
      </button>
    </div>
  );
}

type Sort<K extends string> = { col: K; dir: 1 | -1 };
function sortRows<T, K extends string>(
  rows: T[],
  sort: Sort<K>,
  get: (r: T, k: K) => unknown,
): T[] {
  return [...rows].sort((a, b) => {
    const av = get(a, sort.col);
    const bv = get(b, sort.col);
    if (typeof av === "string" || typeof bv === "string") {
      return String(av ?? "").localeCompare(String(bv ?? "")) * sort.dir;
    }
    return ((Number(av) || 0) - (Number(bv) || 0)) * sort.dir;
  });
}

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

type Load<T> =
  | { kind: "loading" }
  | { kind: "error"; message: string; missing: boolean }
  | { kind: "ready"; data: T };

function errorOf(err: unknown): { kind: "error"; message: string; missing: boolean } {
  const message = err instanceof Error ? err.message : String(err);
  return { kind: "error", message, missing: err instanceof RevenueReadError && err.missing };
}

function useLineRecords(cfg: RevenueConfig): Load<LineRecord[]> {
  const tenantId = useTenantId();
  const [state, setState] = useState<Load<LineRecord[]>>({ kind: "loading" });
  const exclusions = cfg.credit_exclusions.join("|");
  const labels = JSON.stringify(cfg.division_labels);
  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    setState({ kind: "loading" });
    const today = new Date();
    fetchRevenueLines(tenantId, today).then(
      (res) => {
        if (!alive) return;
        const records = buildLineRecords(res.all, res.negative, cfg);
        setState({ kind: "ready", data: completeMonthsOnly(records, today) });
      },
      (err: unknown) => {
        if (alive) setState(errorOf(err));
      },
    );
    return () => {
      alive = false;
    };
    // cfg is rebuilt per render; its inputs are the keys below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, exclusions, labels]);
  return state;
}

interface AccountData {
  accts: AccountRecord[];
  fuel: InvoiceGroupRow[];
  proposal: FscProposal | null;
}

function useAccountRecords(cfg: RevenueConfig, enabled: boolean): Load<AccountData> {
  const tenantId = useTenantId();
  const [state, setState] = useState<Load<AccountData>>({ kind: "loading" });
  const exclusions = cfg.credit_exclusions.join("|");
  const labels = JSON.stringify(cfg.division_labels);
  useEffect(() => {
    if (!enabled || !tenantId) return;
    let alive = true;
    setState({ kind: "loading" });
    const today = new Date();
    fetchRevenueAccounts(tenantId, today).then(
      (res) => {
        if (!alive) return;
        const accts = completeMonthsOnly(
          buildAccountRecords(res.all, res.negative, res.names, cfg),
          today,
        );
        setState({ kind: "ready", data: { accts, fuel: res.fuel, proposal: res.proposal } });
      },
      (err: unknown) => {
        if (alive) setState(errorOf(err));
      },
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tenantId, exclusions, labels]);
  return state;
}

function LoadGate<T>(props: { state: Load<T>; title: string; children: (data: T) => ReactNode }) {
  const { state } = props;
  if (state.kind === "loading") return <Skeleton label={props.title} />;
  if (state.kind === "error") {
    if (state.missing) return <EmptyState title={props.title} caption={GATE_COPY} />;
    return <ErrorState message={state.message} />;
  }
  return <>{props.children(state.data)}</>;
}

// ---------------------------------------------------------------------------
// Account list inside a drill (division, line, credit tile)
// ---------------------------------------------------------------------------

function DrillAccounts(props: {
  cfg: RevenueConfig;
  divisions: string[];
  title: string;
  pred: (r: AccountRecord) => boolean;
  creditsFirst?: boolean;
  onOpen?: (account: string) => void;
}) {
  const state = useAccountRecords(props.cfg, true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<Sort<"name" | "amount" | "credits" | "month_count">>({
    col: props.creditsFirst ? "credits" : "amount",
    dir: props.creditsFirst ? 1 : -1,
  });
  const [limit, more] = useRowLimit(`${props.title}|${search}`);
  return (
    <LoadGate state={state} title={props.title}>
      {(data) => {
        const accts = filterDivisions(data.accts, props.divisions);
        const list = accountsFor(accts, props.pred).filter(
          (a) => !search || a.name.toLowerCase().includes(search.toLowerCase()),
        );
        const cols = ["name", "amount", "credits", "month_count"] as const;
        const sorted = sortRows(list, sort, (r, k) => r[k]);
        const creditTotal = list.reduce((s, a) => s + a.credits, 0);
        return (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <strong style={{ fontSize: 13 }}>{props.title}</strong>
              <span style={note}>
                {int(list.length)} accounts · {money(creditTotal)} in credits
              </span>
              <input
                style={control}
                placeholder="Search accounts"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Table
              head={["Account", "Revenue", "Credits", "Months Active"]}
              onHeadClick={(i) =>
                setSort((s) => ({ col: cols[i], dir: s.col === cols[i] ? (-s.dir as 1 | -1) : -1 }))
              }
              onRowClick={props.onOpen ? (i) => props.onOpen?.(sorted[i].account) : undefined}
              rows={sorted
                .slice(0, limit)
                .map((a) => [a.name, money(a.amount), money(a.credits), int(a.month_count)])}
            />
            <MoreRows shown={Math.min(limit, sorted.length)} total={sorted.length} onMore={more} />
          </div>
        );
      }}
    </LoadGate>
  );
}

function AccountDrill(props: { accts: AccountRecord[]; account: string; onClose: () => void }) {
  const detail = useMemo(
    () => accountDetail(props.accts, props.account),
    [props.accts, props.account],
  );
  const name = props.accts.find((r) => r.account === props.account)?.name ?? props.account;
  return (
    <div style={{ ...tile, marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <strong>{name}</strong>
        <button type="button" style={{ ...control, cursor: "pointer" }} onClick={props.onClose}>
          Close
        </button>
      </div>
      <Note>
        {money(detail.total)} over {detail.months.length} month(s)
      </Note>
      {detail.months.length > 0 ? (
        <Chart
          type="line"
          name={`${name} monthly revenue`}
          format="currency_usd"
          xAxisGranularity="category"
          height={200}
          data={{
            labels: detail.months.map(monthLabel),
            datasets: [
              {
                label: name,
                data: detail.monthly,
                borderColor: PALETTE[1],
                backgroundColor: PALETTE[1],
                tension: 0.2,
              },
            ],
          }}
        />
      ) : null}
      <Table
        head={["Month", "Division", "Line of Business", "Amount"]}
        rows={detail.rows.map((r) => [monthLabel(r.month), r.division, r.line, money(r.amount)])}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Views over line records
// ---------------------------------------------------------------------------

function OverviewView(props: { records: LineRecord[]; month: string; domain: string }) {
  const o = overview(props.records, props.month);
  const tiles: {
    title: string;
    value: number | string | null;
    format: "currency_usd" | "text";
    delta?: number | null;
    periodNote?: string;
  }[] = [
    {
      title: "Total Revenue (all months)",
      value: o.grand_total,
      format: "currency_usd",
      periodNote: `${o.months.length} closed month(s)`,
    },
    {
      title: `${monthLabel(o.month)} Revenue`,
      value: o.latest,
      format: "currency_usd",
      // KPITile reads the change in percent units (5.2 = +5.2%).
      delta: o.mom === null ? null : Math.round(o.mom * 1000) / 10,
      periodNote:
        o.prior !== null && o.prior_month
          ? `vs ${monthLabel(o.prior_month)} ${money(o.prior)}`
          : "No prior month loaded",
    },
    {
      title: "Avg Monthly Revenue",
      value: o.avg_monthly,
      format: "currency_usd",
      periodNote: "Total over the closed months loaded",
    },
    {
      title: "Top Division",
      value: o.top_division?.label ?? null,
      format: "text",
      periodNote: o.top_division ? money(o.top_division.value) : undefined,
    },
    {
      title: "Top Line of Business",
      value: o.top_line?.label ?? null,
      format: "text",
      periodNote: o.top_line ? money(o.top_line.value) : undefined,
    },
  ];
  return (
    <div>
      {o.partial_month ? (
        <div
          style={{
            ...tile,
            borderColor: "var(--flag-yellow)",
            background: "var(--flag-yellow-wash)",
            marginBottom: 10,
            fontSize: 12,
          }}
        >
          {monthLabel(o.partial_month.month)} revenue ({money(o.partial_month.value)}) is under 60%
          of the average of the months before it ({money(o.partial_month.prior_avg)}). Check that
          the month's revenue is fully loaded before reading it as a drop.
        </div>
      ) : (
        <Note>
          Partial-month check:{" "}
          {o.months.length > 1
            ? "the newest closed month is in line with the months before it."
            : "needs two closed months."}
        </Note>
      )}
      <div style={grid(170)}>
        {tiles.map((t) => (
          <KPITile
            key={t.title}
            title={t.title}
            value={t.value}
            format={t.format}
            delta={t.delta ?? null}
            isGated={t.value === null}
            gateCopy={GATE_COPY}
            periodNote={t.periodNote}
            domain={props.domain}
          />
        ))}
      </div>
    </div>
  );
}

function TrendBars(props: { records: LineRecord[]; name: string }) {
  const rows = monthlyTotals(props.records);
  return (
    <Chart
      type="bar"
      name={props.name}
      format="currency_usd"
      xAxisGranularity="category"
      height={240}
      data={{
        labels: rows.map((r) => monthLabel(r.month)),
        datasets: [
          {
            label: "Earned revenue",
            data: rows.map((r) => r.value),
            backgroundColor: PALETTE[0],
            borderColor: PALETTE[0],
          },
        ],
      }}
    />
  );
}

function DivisionMonthTiles(props: { records: LineRecord[]; month: string; order: string[] }) {
  const tiles = divisionMonth(props.records, props.month, props.order);
  return (
    <>
      <Note>{monthLabel(props.month)}. Share is of the month's total.</Note>
      <div style={grid(160)}>
        {tiles.map((t, i) => (
          <div
            key={t.slug}
            style={{ ...tile, borderTop: `3px solid ${PALETTE[i % PALETTE.length]}` }}
          >
            <div style={{ fontSize: 12, fontWeight: 700 }}>{t.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{money(t.value)}</div>
            <div style={note}>{pct(t.share)} of month</div>
          </div>
        ))}
      </div>
    </>
  );
}

function MonthTableView(props: { records: LineRecord[]; order: string[] }) {
  const t = monthTable(props.records, props.order);
  const headers = ["Month", "Total Revenue", "MoM %", ...t.divisions.map((d) => d.label)];
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
        <CsvButton
          filename="revenue_by_month.csv"
          headers={headers}
          rows={t.rows.map((r) => [
            monthLabel(r.month),
            r.total,
            r.mom === null || r.mom === undefined ? "" : Number((r.mom * 100).toFixed(1)),
            ...t.divisions.map((d) => r.by_division[d.slug] ?? 0),
          ])}
        />
      </div>
      <Table
        head={headers}
        rows={t.rows.map((r) => [
          monthLabel(r.month),
          money(r.total),
          r.mom === null || r.mom === undefined ? "-" : `${r.mom >= 0 ? "+" : ""}${pct(r.mom)}`,
          ...t.divisions.map((d) => money(r.by_division[d.slug])),
        ])}
        foot={[
          "Total",
          money(t.totals.total),
          "-",
          ...t.divisions.map((d) => money(t.totals.by_division[d.slug])),
        ]}
      />
    </>
  );
}

function SliceDrill(props: {
  records: LineRecord[];
  kind: "division" | "line";
  keyValue: string;
  label: string;
  order: string[];
  cfg: RevenueConfig;
  divisions: string[];
}) {
  const d = drill(props.records, props.kind, props.keyValue, props.order);
  const months = d.rows.map((r) => r.month);
  return (
    <div style={{ ...tile, marginTop: 12 }}>
      <strong>{props.label}</strong>
      <Note>
        {money(d.total)} across {d.rows.length} month(s), split by{" "}
        {props.kind === "division" ? "line of business" : "division"}
      </Note>
      <Chart
        type="stackedBar"
        name={`${props.label} by month`}
        format="currency_usd"
        xAxisGranularity="category"
        height={220}
        data={{
          labels: months.map(monthLabel),
          datasets: d.columns.map((c, i) => ({
            label: c.label,
            data: d.rows.map((r) => r.parts[c.key] ?? 0),
            backgroundColor: PALETTE[i % PALETTE.length],
            borderColor: PALETTE[i % PALETTE.length],
          })),
        }}
      />
      <Table
        head={["Month", ...d.columns.map((c) => c.label), "Total"]}
        rows={d.rows.map((r) => [
          monthLabel(r.month),
          ...d.columns.map((c) => money(r.parts[c.key])),
          money(r.total),
        ])}
      />
      <DrillAccounts
        cfg={props.cfg}
        divisions={props.divisions}
        title={`Accounts in ${props.label}`}
        pred={(r) => (props.kind === "division" ? r.divSlug : r.lob) === props.keyValue}
      />
    </div>
  );
}

function SliceTiles(props: {
  records: LineRecord[];
  kind: "division" | "line";
  order: string[];
  cfg: RevenueConfig;
  divisions: string[];
}) {
  const [open, setOpen] = useState<string | null>(null);
  const tiles =
    props.kind === "division"
      ? divisionTiles(props.records, props.order).map((t) => ({ ...t, key: t.slug }))
      : lineTiles(props.records);
  const openTile = tiles.find((t) => t.key === open) ?? null;
  return (
    <>
      <Note>Every closed month loaded. Click a tile for its months and accounts.</Note>
      <div style={grid(170)}>
        {tiles.map((t, i) => (
          <div
            key={t.key}
            role="button"
            tabIndex={0}
            onClick={() => setOpen((o) => (o === t.key ? null : t.key))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen((o) => (o === t.key ? null : t.key));
              }
            }}
            style={{
              ...tile,
              cursor: "pointer",
              borderTop: `3px solid ${PALETTE[i % PALETTE.length]}`,
              outline: open === t.key ? "2px solid var(--brand-teal)" : undefined,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700 }}>{t.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{money(t.value)}</div>
            <div style={note}>{pct(t.share)} of total</div>
            <div style={note}>{int(t.lines)} invoice lines</div>
          </div>
        ))}
      </div>
      {openTile ? (
        <SliceDrill
          records={props.records}
          kind={props.kind}
          keyValue={openTile.key}
          label={openTile.label}
          order={props.order}
          cfg={props.cfg}
          divisions={props.divisions}
        />
      ) : null}
    </>
  );
}

function DivisionTrend(props: { records: LineRecord[]; order: string[]; name: string }) {
  const s = divisionSeries(props.records, props.order);
  return (
    <Chart
      type="line"
      name={props.name}
      format="currency_usd"
      xAxisGranularity="category"
      height={260}
      data={{
        labels: s.months.map(monthLabel),
        datasets: s.series.map((x, i) => ({
          label: x.label,
          data: x.values,
          borderColor: PALETTE[i % PALETTE.length],
          backgroundColor: PALETTE[i % PALETTE.length],
          tension: 0.2,
        })),
      }}
    />
  );
}

function CreditsSummaryView(props: { records: LineRecord[]; month: string; domain: string }) {
  const s = creditsSummary(props.records, props.month);
  return (
    <div style={grid(170)}>
      <KPITile
        title="Total Credits (all months)"
        value={s.total_credits}
        format="currency_usd"
        periodNote="Every negative line except the customer's excluded lines"
        domain={props.domain}
      />
      <KPITile
        title={`${monthLabel(s.month)} Credits`}
        value={s.month_credits}
        format="currency_usd"
        domain={props.domain}
      />
      <KPITile
        title="Credits as % of Gross Revenue"
        value={s.pct_of_gross}
        format="percent"
        isGated={s.pct_of_gross === null}
        gateCopy={GATE_COPY}
        periodNote="Credits over gross (net revenue with the credits added back)"
        domain={props.domain}
      />
    </div>
  );
}

function CreditsTrend(props: { records: LineRecord[]; name: string }) {
  const rows = creditsMonthly(props.records);
  return (
    <Chart
      type="bar"
      name={props.name}
      format="currency_usd"
      xAxisGranularity="category"
      height={220}
      data={{
        labels: rows.map((r) => monthLabel(r.month)),
        datasets: [
          {
            label: "Credits",
            data: rows.map((r) => r.credits),
            backgroundColor: PALETTE[6],
            borderColor: PALETTE[6],
          },
        ],
      }}
    />
  );
}

function CreditTiles(props: {
  records: LineRecord[];
  kind: "division" | "line";
  month: string;
  order: string[];
  cfg: RevenueConfig;
  divisions: string[];
}) {
  const [scope, setScope] = useState<"all" | "month">("all");
  const [open, setOpen] = useState<string | null>(null);
  const month = scope === "month" ? props.month : null;
  const tiles = creditsBy(props.records, props.kind, month, props.order);
  const openTile = tiles.find((t) => t.key === open) ?? null;
  const what = props.kind === "division" ? "division" : "line";
  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <select
          style={control}
          value={scope}
          onChange={(e) => setScope(e.target.value as "all" | "month")}
        >
          <option value="all">All months</option>
          <option value="month">{monthLabel(props.month)}</option>
        </select>
        <CsvButton
          filename={`credits_by_${props.kind}_${scope === "all" ? "all" : props.month}.csv`}
          headers={[
            props.kind === "division" ? "Division" : "Line of Business",
            "Credits",
            "Gross Revenue",
            "% of Gross",
          ]}
          rows={tiles.map((t) => [
            t.label,
            t.credits,
            t.gross,
            t.pct === null ? "n/a" : Number((t.pct * 100).toFixed(1)),
          ])}
        />
      </div>
      <div style={grid(160)}>
        {tiles.map((t, i) => (
          <div
            key={t.key}
            role="button"
            tabIndex={0}
            onClick={() => setOpen((o) => (o === t.key ? null : t.key))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen((o) => (o === t.key ? null : t.key));
              }
            }}
            style={{
              ...tile,
              cursor: "pointer",
              borderTop: `3px solid ${PALETTE[i % PALETTE.length]}`,
              outline: open === t.key ? "2px solid var(--brand-teal)" : undefined,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700 }}>{t.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{money(t.credits)}</div>
            <div style={note}>
              {pct(t.pct)} of that {what}'s gross{month ? " this month" : ""}
            </div>
          </div>
        ))}
      </div>
      {openTile ? (
        <DrillAccounts
          cfg={props.cfg}
          divisions={props.divisions}
          title={`${openTile.label}, ${month ? monthLabel(month) : "all months"}`}
          creditsFirst
          pred={(r) =>
            (props.kind === "division" ? r.divSlug : r.lob) === openTile.key &&
            (!month || r.mk === month)
          }
        />
      ) : null}
    </>
  );
}

function GrossVsCredits(props: { records: LineRecord[] }) {
  const rows = creditsMonthly(props.records);
  const g = rows.reduce((s, r) => s + r.gross, 0);
  const c = rows.reduce((s, r) => s + r.credits, 0);
  const n = rows.reduce((s, r) => s + r.net, 0);
  const headers = ["Month", "Gross Revenue", "Credits", "Net", "Credits % of Gross"];
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
        <CsvButton
          filename="gross_vs_credits.csv"
          headers={headers}
          rows={rows.map((r) => [
            monthLabel(r.month),
            r.gross,
            r.credits,
            r.net,
            r.pct === null ? "" : Number((r.pct * 100).toFixed(1)),
          ])}
        />
      </div>
      <Table
        head={headers}
        rows={rows.map((r) => [
          monthLabel(r.month),
          money(r.gross),
          money(r.credits),
          money(r.net),
          pct(r.pct),
        ])}
        foot={[
          "Total",
          money(g),
          money(c),
          money(n),
          pct(Math.abs(g) < 1 ? null : Math.abs(c) / g),
        ]}
      />
    </>
  );
}

function MiscTable(props: { records: LineRecord[]; order: string[] }) {
  const t = miscByDivision(props.records, props.order);
  if (t.rows.length === 0) {
    return (
      <Note>
        No revenue on lines without a recognised line of business in the closed months loaded.
      </Note>
    );
  }
  return (
    <Table
      head={["Month", ...t.divisions.map((d) => d.label), "Total"]}
      rows={t.rows.map((r) => [
        monthLabel(r.month),
        ...t.divisions.map((d) => money(r.by_division[d.slug])),
        money(r.total),
      ])}
      foot={[
        "Total",
        ...t.divisions.map((d) => money(t.totals.by_division[d.slug])),
        money(t.totals.total),
      ]}
    />
  );
}

function AllDataView(props: { records: LineRecord[]; order: string[] }) {
  const [division, setDivision] = useState("");
  const [line, setLine] = useState("");
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [limit, more] = useRowLimit(`${division}|${line}|${month}|${search}`);
  const rows = allData(props.records, { division, line, month, search });
  const sum = rows.reduce((s, r) => s + r.amount, 0);
  const headers = ["Month", "Division", "Line of Business", "Amount"];
  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <input
          style={control}
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={control} value={division} onChange={(e) => setDivision(e.target.value)}>
          <option value="">All Divisions</option>
          {divisionsOf(props.records, props.order).map((d) => (
            <option key={d.slug} value={d.slug}>
              {d.label}
            </option>
          ))}
        </select>
        <select style={control} value={line} onChange={(e) => setLine(e.target.value)}>
          <option value="">All Lines</option>
          {linesOf(props.records).map((l) => (
            <option key={l} value={l}>
              {lineLabel(l)}
            </option>
          ))}
        </select>
        <select style={control} value={month} onChange={(e) => setMonth(e.target.value)}>
          <option value="">All Months</option>
          {monthsOf(props.records).map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </select>
        <CsvButton
          filename="revenue_all_data.csv"
          headers={headers}
          rows={rows.map((r) => [monthLabel(r.month), r.division, r.line, r.amount])}
        />
      </div>
      <Table
        head={headers}
        rows={rows
          .slice(0, limit)
          .map((r) => [monthLabel(r.month), r.division, r.line, money(r.amount)])}
        foot={[`Total (${int(rows.length)} rows)`, "", "", money(sum)]}
      />
      <MoreRows shown={Math.min(limit, rows.length)} total={rows.length} onMore={more} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Category group views (Roll Off, Front Load, Residential)
// ---------------------------------------------------------------------------

function CategorySummary(props: {
  groupName: string;
  catRecords: CategoryRecord[];
  month: string;
  domain: string;
}) {
  const m = monthLabel(props.month);
  const d = props.domain;
  if (props.groupName === "roll_off") {
    const s = rollOffSummary(props.catRecords, props.month);
    return (
      <div style={grid(170)}>
        <KPITile
          title="Completed Hauls"
          value={s.hauls}
          format="integer"
          periodNote={`${m} · haul charge lines`}
          domain={d}
        />
        <KPITile
          title="Haul Revenue"
          value={s.haul_revenue}
          format="currency_usd"
          periodNote={`${m} · haul + disposal + overage`}
          domain={d}
        />
        <KPITile
          title="Non-Haul Revenue"
          value={s.non_haul_revenue}
          format="currency_usd"
          periodNote={m}
          domain={d}
        />
        <KPITile
          title="Haul Revenue per Haul"
          value={s.haul_revenue_per_haul}
          format="currency_usd"
          isGated={s.haul_revenue_per_haul === null}
          gateCopy={GATE_COPY}
          periodNote={m}
          domain={d}
        />
        <KPITile
          title="Blended Revenue per Haul"
          value={s.blended_revenue_per_haul}
          format="currency_usd"
          isGated={s.blended_revenue_per_haul === null}
          gateCopy={GATE_COPY}
          periodNote={`${m} · every roll off line over hauls`}
          domain={d}
        />
      </div>
    );
  }
  if (props.groupName === "front_load") {
    const s = frontLoadSummary(props.catRecords, props.month);
    return (
      <div style={grid(170)}>
        <KPITile
          title="Core Service Revenue"
          value={s.core}
          format="currency_usd"
          periodNote={m}
          domain={d}
        />
        <KPITile
          title="Fuel Surcharge Revenue"
          value={s.fuel_surcharge}
          format="currency_usd"
          periodNote={m}
          domain={d}
        />
        <KPITile
          title="FSC % of Total"
          value={s.fsc_share}
          format="percent"
          isGated={s.fsc_share === null}
          gateCopy={GATE_COPY}
          periodNote={m}
          domain={d}
        />
        <KPITile
          title="Extra Charges Revenue"
          value={s.extra}
          format="currency_usd"
          periodNote={m}
          domain={d}
        />
        <KPITile
          title="Extra % of Total"
          value={s.extra_share}
          format="percent"
          isGated={s.extra_share === null}
          gateCopy={GATE_COPY}
          periodNote={m}
          domain={d}
        />
      </div>
    );
  }
  const s = residentialSummary(props.catRecords, props.month);
  return (
    <div style={grid(170)}>
      <KPITile
        title="Core Service Revenue"
        value={s.core}
        format="currency_usd"
        periodNote={m}
        domain={d}
      />
      <KPITile
        title="Fees and Adjustments"
        value={s.fees}
        format="currency_usd"
        periodNote={m}
        domain={d}
      />
      <KPITile
        title="Total Revenue"
        value={s.total}
        format="currency_usd"
        periodNote={m}
        domain={d}
      />
      <KPITile
        title="Fees % of Total"
        value={s.fees_share}
        format="percent"
        isGated={s.fees_share === null}
        gateCopy={GATE_COPY}
        periodNote={m}
        domain={d}
      />
    </div>
  );
}

function TopDriversView(props: {
  group: CategoryGroup;
  catRecords: CategoryRecord[];
  month: string;
}) {
  const cats = topDrivers(props.catRecords, props.group, props.month);
  return (
    <>
      <Note>
        {monthLabel(props.month)}. The three charge descriptions with the most dollars in each
        category.
      </Note>
      <div style={grid(200)}>
        {cats.map((c, i) => (
          <div
            key={c.category}
            style={{ ...tile, borderTop: `3px solid ${PALETTE[i % PALETTE.length]}` }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{c.category}</div>
            {c.top.length === 0 ? (
              <div style={note}>No activity this month.</div>
            ) : (
              c.top.map((t) => (
                <div
                  key={t.description}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 8,
                    fontSize: 12,
                    padding: "2px 0",
                  }}
                >
                  <span>{t.description || "(no description)"}</span>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>{money(t.amount)}</span>
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function WorkingDaysView(props: {
  groupName: string;
  catRecords: CategoryRecord[];
  rule: string | null;
}) {
  if (props.rule === null) {
    return (
      <EmptyState
        title="Revenue per Working Day"
        caption={`${GATE_COPY}: this customer's working-day rule`}
      />
    );
  }
  if (props.groupName === "roll_off") {
    const rows = workingDayView(
      props.catRecords,
      rollOffSummary,
      {
        hauls: (s) => s.hauls,
        haul: (s) => s.haul_revenue,
        total: (s) => s.total,
      },
      props.rule,
    );
    return (
      <Table
        head={[
          "Month",
          "Working Days",
          "Completed Hauls",
          "Hauls / Day",
          "Haul Rev / Day",
          "Total Rev / Day",
        ]}
        rows={rows.map((r) => [
          monthLabel(r.month),
          int(r.working_days),
          int(r.summary.hauls),
          r.per_day.hauls === null ? "-" : r.per_day.hauls.toFixed(1),
          money(r.per_day.haul),
          money(r.per_day.total),
        ])}
      />
    );
  }
  if (props.groupName === "front_load") {
    const rows = workingDayView(
      props.catRecords,
      frontLoadSummary,
      {
        core: (s) => s.core,
        fsc: (s) => s.fuel_surcharge,
        extra: (s) => s.extra,
        total: (s) => s.total,
      },
      props.rule,
    );
    return (
      <Table
        head={["Month", "Working Days", "Core / Day", "FSC / Day", "Extra / Day", "Total / Day"]}
        rows={rows.map((r) => [
          monthLabel(r.month),
          int(r.working_days),
          money(r.per_day.core),
          money(r.per_day.fsc),
          money(r.per_day.extra),
          money(r.per_day.total),
        ])}
      />
    );
  }
  const rows = workingDayView(
    props.catRecords,
    residentialSummary,
    {
      core: (s) => s.core,
      fees: (s) => s.fees,
      total: (s) => s.total,
    },
    props.rule,
  );
  return (
    <Table
      head={["Month", "Working Days", "Core / Day", "Fees / Day", "Total / Day"]}
      rows={rows.map((r) => [
        monthLabel(r.month),
        int(r.working_days),
        money(r.per_day.core),
        money(r.per_day.fees),
        money(r.per_day.total),
      ])}
    />
  );
}

function BreakdownTable(props: {
  title: string;
  catRecords: CategoryRecord[];
  categories: string[];
  month: string | null;
  negativeOnly: boolean;
}) {
  const b = breakdown(props.catRecords, props.categories, props.month, props.negativeOnly);
  if (b.sections.length === 0) {
    return (
      <div style={{ marginTop: 10 }}>
        <strong style={{ fontSize: 13 }}>{props.title}</strong>
        <Note>No lines in this selection.</Note>
      </div>
    );
  }
  const head = ["Description", ...b.months.map(monthLabel), "Total"];
  const rows: ReactNode[][] = [];
  const csv: (string | number)[][] = [];
  for (const s of b.sections) {
    rows.push([<strong key="c">{s.category}</strong>, ...b.months.map(() => ""), ""]);
    for (const r of s.rows) {
      rows.push([
        r.description || "(no description)",
        ...b.months.map((m) => money(r.by_month[m])),
        money(r.total),
      ]);
      csv.push([s.category, r.description, ...b.months.map((m) => r.by_month[m] ?? 0), r.total]);
    }
    rows.push([
      <em key="s">{s.category} subtotal</em>,
      ...b.months.map((m) => money(s.subtotal.by_month[m])),
      money(s.subtotal.total),
    ]);
  }
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 4,
        }}
      >
        <strong style={{ fontSize: 13 }}>{props.title}</strong>
        <CsvButton
          filename={`${props.title.toLowerCase().replace(/\W+/g, "_")}.csv`}
          headers={["Category", "Description", ...b.months.map(monthLabel), "Total"]}
          rows={csv}
        />
      </div>
      <Table head={head} rows={rows} />
    </div>
  );
}

function BreakdownView(props: {
  groupName: string;
  group: CategoryGroup;
  catRecords: CategoryRecord[];
  months: string[];
}) {
  const [month, setMonth] = useState("");
  const cats = groupCategories(props.group);
  const m = month || null;
  const tables: { title: string; categories: string[]; neg: boolean }[] = [];
  if (props.groupName === "roll_off") {
    const haul = props.group.rules
      .filter((r) =>
        ["haul_charge_revenue", "disposal_charge_revenue", "overage_charge_revenue"].includes(
          r.maps_to,
        ),
      )
      .map((r) => r.category);
    const nonHaul = cats.filter((c) => !haul.includes(c));
    tables.push({
      title: "Haul Breakdown",
      categories: cats.filter((c) => haul.includes(c)),
      neg: false,
    });
    tables.push({ title: "Non-Haul Breakdown", categories: nonHaul, neg: false });
  } else {
    for (const c of cats) tables.push({ title: `${c} Breakdown`, categories: [c], neg: false });
  }
  tables.push({ title: "Adjustments (negative lines)", categories: cats, neg: true });
  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select style={control} value={month} onChange={(e) => setMonth(e.target.value)}>
          <option value="">All months</option>
          {props.months.map((x) => (
            <option key={x} value={x}>
              {monthLabel(x)}
            </option>
          ))}
        </select>
        <span style={note}>Dollars by the exact charge description, by month.</span>
      </div>
      {tables.map((t) => (
        <BreakdownTable
          key={t.title}
          title={t.title}
          catRecords={props.catRecords}
          categories={t.categories}
          month={m}
          negativeOnly={t.neg}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Account views
// ---------------------------------------------------------------------------

function MoversView(props: { accts: AccountRecord[]; month: string }) {
  const prior = previousMonth(props.month);
  if (!prior || !props.accts.some((r) => r.mk === prior)) {
    return <Note>Needs two closed months of account-level revenue to compare.</Note>;
  }
  const mv = movers(props.accts, props.month, prior);
  const col = (title: string, list: typeof mv.gainers) => (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{title}</div>
      {list.length === 0 ? (
        <div style={note}>No change.</div>
      ) : (
        list.map((m) => (
          <div
            key={m.account}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              padding: "3px 0",
              borderBottom: "1px solid var(--border-subtle)",
            }}
          >
            <div>
              <div style={{ fontSize: 13 }}>{m.name}</div>
              <div style={note}>{m.div}</div>
            </div>
            <Signed n={m.delta} />
          </div>
        ))
      )}
    </div>
  );
  return (
    <>
      <Note>
        {monthLabel(props.month)} against {monthLabel(prior)}, by account.
      </Note>
      <div style={grid(240)}>
        {col("Top Gainers", mv.gainers)}
        {col("Top Decliners", mv.decliners)}
      </div>
    </>
  );
}

function AccountsView(props: { accts: AccountRecord[]; order: string[] }) {
  const [search, setSearch] = useState("");
  const [division, setDivision] = useState("");
  const [month, setMonth] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [sort, setSort] = useState<
    Sort<"name" | "divisions" | "lines" | "amount" | "credits" | "month_count">
  >({
    col: "amount",
    dir: -1,
  });
  const [limit, more] = useRowLimit(`${search}|${division}|${month}`);
  const list = accountsSummary(props.accts, month || null).filter(
    (a) =>
      (!division || a.division_list.includes(division)) &&
      (!search || a.name.toLowerCase().includes(search.toLowerCase())),
  );
  const cols = ["name", "divisions", "lines", "amount", "credits", "month_count"] as const;
  const sorted = sortRows(list, sort, (r, k) => r[k]);
  const divs = divisionsOf(props.accts, props.order);
  const headers = [
    "Account",
    "Division(s)",
    "Line(s)",
    "Total Revenue",
    "Total Credits",
    month ? "Active This Month" : "Months Active",
  ];
  return (
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <input
          style={control}
          placeholder="Search accounts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={control} value={division} onChange={(e) => setDivision(e.target.value)}>
          <option value="">All Divisions</option>
          {divs.map((d) => (
            <option key={d.slug} value={d.label}>
              {d.label}
            </option>
          ))}
        </select>
        <select style={control} value={month} onChange={(e) => setMonth(e.target.value)}>
          <option value="">All months</option>
          {monthsOf(props.accts).map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </select>
        <CsvButton
          filename={`accounts_${month || "all_months"}.csv`}
          headers={headers}
          rows={sorted.map((a) => [
            a.name,
            a.divisions,
            a.lines,
            a.amount,
            a.credits,
            a.month_count,
          ])}
        />
        <span style={note}>
          {int(list.length)} account(s){month ? ` in ${monthLabel(month)}` : ", all months"}. Click
          a row for its history.
        </span>
      </div>
      <Table
        head={headers}
        onHeadClick={(i) =>
          setSort((s) => ({ col: cols[i], dir: s.col === cols[i] ? (-s.dir as 1 | -1) : -1 }))
        }
        onRowClick={(i) => setOpen(sorted[i].account)}
        rows={sorted
          .slice(0, limit)
          .map((a) => [
            a.name,
            a.divisions,
            a.lines,
            money(a.amount),
            money(a.credits),
            int(a.month_count),
          ])}
      />
      <MoreRows shown={Math.min(limit, sorted.length)} total={sorted.length} onMore={more} />
      {open ? (
        <AccountDrill accts={props.accts} account={open} onClose={() => setOpen(null)} />
      ) : null}
    </>
  );
}

function TopCreditAccounts(props: { accts: AccountRecord[] }) {
  const rows = topCreditAccounts(props.accts);
  if (rows.length === 0) return <Note>No credits recorded for any account.</Note>;
  return (
    <Table
      head={["Account", "Division", "Total Credits"]}
      rows={rows.map((a) => [a.name, a.divisions, money(a.credits)])}
    />
  );
}

function FuelSurchargeView(props: {
  accts: AccountRecord[];
  fuel: InvoiceGroupRow[];
  cfg: RevenueConfig;
  /** From the tenant's private rate inputs at runtime; never from the manifest. */
  proposal: FscProposal | null;
  month: string | null;
  divisions: string[];
  services: string[];
}) {
  const [search, setSearch] = useState("");
  const [limit, more] = useRowLimit(`${search}|${props.month ?? ""}`);
  // Both sides are cut to the page's divisions and service lines inside
  // fuelSurchargeByAccount, from the unfiltered account records.
  const rows = fuelSurchargeByAccount(
    props.fuel,
    props.accts,
    props.cfg.fuel_surcharge_names,
    props.month,
    props.proposal,
    { divisions: props.divisions, services: props.services },
  ).filter((r) => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const proposal = props.proposal;
  const monthly = props.month !== null;
  const headers = monthly
    ? [
        "Account",
        "FSC Charge",
        "Total Billed",
        "FSC % of Total",
        "Proposed FSC %",
        "Proposed FSC Charge",
        "Proposed Total Billed",
        "FSC Share Increase",
      ]
    : ["Account", "FSC Charge", "Total Billed", "FSC % of Total", "Months with FSC"];
  const fscTotal = rows.reduce((s, r) => s + r.fsc, 0);
  const billedTotal = rows.reduce((s, r) => s + r.billed, 0);
  return (
    <>
      <Note>
        {monthly ? monthLabel(props.month) : "Every closed month loaded"}. Fuel surcharge lines are
        the customer's own surcharge names ({props.cfg.fuel_surcharge_names.join(", ")}). Total
        billed is every earned line on the account.
        {monthly && !proposal
          ? " Proposed columns await this customer's fuel surcharge pricing rule."
          : ""}
      </Note>
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <input
          style={control}
          placeholder="Search accounts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <CsvButton
          filename={`fuel_surcharge_${props.month ?? "all_months"}.csv`}
          headers={headers}
          rows={rows.map((r) =>
            monthly
              ? [
                  r.name,
                  r.fsc,
                  r.billed,
                  r.share,
                  r.proposed_share,
                  r.proposed_charge,
                  r.proposed_billed,
                  r.share_increase,
                ]
              : [r.name, r.fsc, r.billed, r.share, r.month_count],
          )}
        />
      </div>
      <Table
        head={headers}
        rows={rows
          .slice(0, limit)
          .map((r) =>
            monthly
              ? [
                  r.name,
                  money(r.fsc),
                  money(r.billed),
                  pct(r.share, 2),
                  proposal ? pct(r.proposed_share, 2) : "Awaiting rule",
                  proposal ? money(r.proposed_charge) : "-",
                  proposal ? money(r.proposed_billed) : "-",
                  proposal && r.share_increase !== null
                    ? `${(r.share_increase * 100).toFixed(2)} pts`
                    : "-",
                ]
              : [r.name, money(r.fsc), money(r.billed), pct(r.share, 2), int(r.month_count)],
          )}
        foot={
          monthly
            ? [
                "Total",
                money(fscTotal),
                money(billedTotal),
                pct(billedTotal ? fscTotal / billedTotal : null, 2),
                "",
                "",
                "",
                "",
              ]
            : [
                "Total",
                money(fscTotal),
                money(billedTotal),
                pct(billedTotal ? fscTotal / billedTotal : null, 2),
                "",
              ]
        }
      />
      <MoreRows shown={Math.min(limit, rows.length)} total={rows.length} onMore={more} />
    </>
  );
}

function AccountTotalsView(props: { accts: AccountRecord[]; month: string }) {
  const [search, setSearch] = useState("");
  const [limit, more] = useRowLimit(search);
  const rows = accountTotals(props.accts, props.month).filter(
    (r) => !search || r.name.toLowerCase().includes(search.toLowerCase()),
  );
  const headers = [
    "Account",
    "Total Billed (all months)",
    `Total Billed (${monthLabel(props.month)})`,
  ];
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <input
          style={control}
          placeholder="Search accounts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <CsvButton
          filename="account_totals.csv"
          headers={headers}
          rows={rows.map((r) => [r.name, r.all_time, r.month])}
        />
      </div>
      <Table
        head={headers}
        rows={rows.slice(0, limit).map((r) => [r.name, money(r.all_time), money(r.month)])}
      />
      <MoreRows shown={Math.min(limit, rows.length)} total={rows.length} onMore={more} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

const ACCOUNT_VIEWS = new Set([
  "movers",
  "accounts",
  "top_credit_accounts",
  "fuel_surcharge_month",
  "fuel_surcharge_all",
  "account_totals",
]);
const GROUP_VIEWS = new Set(["category_summary", "top_drivers", "working_days", "breakdown"]);

export default function RevenueTabWidget(props: { widget: ManifestWidget }) {
  const { widget } = props;
  const cfg = useMemo(() => normalizeRevenueConfig(widget.revenue_tab), [widget.revenue_tab]);
  const filters = useGlobalFilters();
  const divisionKey = filters.divisions.join(",");
  const serviceKey = filters.services.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const divisions = useMemo(() => filters.divisions, [divisionKey]);
  const order = useMemo(() => widget.revenue_tab?.division_order ?? [], [widget.revenue_tab]);
  const view = cfg.view;
  const isAccountView = ACCOUNT_VIEWS.has(view);
  const lines = useLineRecords(cfg);
  const accounts = useAccountRecords(cfg, isAccountView);
  const domain = widgetDomain(widget);

  const lineRecords = useMemo(() => {
    if (lines.kind !== "ready") return null;
    return filterDivisions(lines.data, divisions);
  }, [lines, divisions]);
  const scoped = useMemo(() => {
    if (!lineRecords) return null;
    // The all-line views follow the page's service chip; the group views keep
    // their population (a residential account is residential on any line).
    return GROUP_VIEWS.has(view) ? lineRecords : filterServices(lineRecords, filters.services);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineRecords, view, serviceKey]);
  const today = new Date();
  const months = scoped ? monthsOf(scoped) : [];
  const month = selectedMonth(filters.month, months, today) ?? "";

  if (isAccountView) {
    return (
      <LoadGate state={accounts} title={widget.name}>
        {(data) => {
          const accts = filterServices(filterDivisions(data.accts, divisions), filters.services);
          if (accts.length === 0) return <EmptyState title={widget.name} caption={GATE_COPY} />;
          const m = selectedMonth(filters.month, monthsOf(accts), today) ?? "";
          switch (view) {
            case "movers":
              return <MoversView accts={accts} month={m} />;
            case "accounts":
              return <AccountsView accts={accts} order={order} />;
            case "top_credit_accounts":
              return <TopCreditAccounts accts={accts} />;
            case "fuel_surcharge_month":
              return (
                <FuelSurchargeView
                  accts={data.accts}
                  fuel={data.fuel}
                  cfg={cfg}
                  proposal={data.proposal}
                  month={m}
                  divisions={divisions}
                  services={filters.services}
                />
              );
            case "fuel_surcharge_all":
              return (
                <FuelSurchargeView
                  accts={data.accts}
                  fuel={data.fuel}
                  cfg={cfg}
                  proposal={data.proposal}
                  month={null}
                  divisions={divisions}
                  services={filters.services}
                />
              );
            case "account_totals":
              return <AccountTotalsView accts={accts} month={m} />;
            default:
              return <ErrorState message={`Unknown Revenue view on ${widget.id}`} />;
          }
        }}
      </LoadGate>
    );
  }

  if (lines.kind !== "ready" || !scoped) {
    return (
      <LoadGate state={lines} title={widget.name}>
        {() => null}
      </LoadGate>
    );
  }
  if (scoped.length === 0) {
    const chipEmptied = filters.services.length > 0 && (lineRecords?.length ?? 0) > 0;
    return (
      <EmptyState
        title={widget.name}
        caption={chipEmptied ? "No revenue on the selected service lines." : GATE_COPY}
      />
    );
  }

  if (GROUP_VIEWS.has(view)) {
    const group = cfg.group ? cfg.groups[cfg.group] : undefined;
    if (!group || !cfg.group) {
      return (
        <EmptyState
          title={widget.name}
          caption={`${GATE_COPY}: this customer's revenue categories`}
        />
      );
    }
    const catRecords = categoryRecords(scoped, group);
    if (catRecords.length === 0) {
      return <EmptyState title={widget.name} caption={GATE_COPY} />;
    }
    const groupMonths = monthsOf(catRecords);
    const gm = selectedMonth(filters.month, groupMonths, today) ?? "";
    switch (view) {
      case "category_summary":
        return (
          <CategorySummary
            groupName={cfg.group}
            catRecords={catRecords}
            month={gm}
            domain={domain}
          />
        );
      case "top_drivers":
        return <TopDriversView group={group} catRecords={catRecords} month={gm} />;
      case "working_days":
        return (
          <WorkingDaysView groupName={cfg.group} catRecords={catRecords} rule={cfg.working_days} />
        );
      case "breakdown":
        return (
          <BreakdownView
            groupName={cfg.group}
            group={group}
            catRecords={catRecords}
            months={groupMonths}
          />
        );
      default:
        return <ErrorState message={`Unknown Revenue view on ${widget.id}`} />;
    }
  }

  switch (view) {
    case "overview":
      return <OverviewView records={scoped} month={month} domain={domain} />;
    case "trend_bars":
      return <TrendBars records={scoped} name={widget.name} />;
    case "division_month":
      return <DivisionMonthTiles records={scoped} month={month} order={order} />;
    case "month_table":
      return <MonthTableView records={scoped} order={order} />;
    case "division_tiles":
      return (
        <SliceTiles
          records={scoped}
          kind="division"
          order={order}
          cfg={cfg}
          divisions={divisions}
        />
      );
    case "division_trend":
      return <DivisionTrend records={scoped} order={order} name={widget.name} />;
    case "line_tiles":
      return (
        <SliceTiles records={scoped} kind="line" order={order} cfg={cfg} divisions={divisions} />
      );
    case "credits_summary":
      return <CreditsSummaryView records={scoped} month={month} domain={domain} />;
    case "credits_trend":
      return <CreditsTrend records={scoped} name={widget.name} />;
    case "credits_by_division":
      return (
        <CreditTiles
          records={scoped}
          kind="division"
          month={month}
          order={order}
          cfg={cfg}
          divisions={divisions}
        />
      );
    case "credits_by_line":
      return (
        <CreditTiles
          records={scoped}
          kind="line"
          month={month}
          order={order}
          cfg={cfg}
          divisions={divisions}
        />
      );
    case "gross_vs_credits":
      return <GrossVsCredits records={scoped} />;
    case "misc_by_division":
      return <MiscTable records={lineRecords ?? scoped} order={order} />;
    case "all_data":
      return <AllDataView records={scoped} order={order} />;
    default:
      return <ErrorState message={`Unknown Revenue view on ${widget.id}`} />;
  }
}
